import { formatNumber } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

import { CoachingTrendData } from '../../card--my-coaching-card/models/coaching-trend-data.model';
import { Legend } from '../../card--my-coaching-card/models/legend.model';
import { AxisDataAxisPath } from '../../scorecard-mi2/chart/axis-data-axis-path';
import { AxisLeftTick } from '../../scorecard-mi2/chart/axis-left-tick';
import { Rect } from '../../scorecard-mi2/chart/util';
import { TrendDialogMode } from '../../scorecard-mi2/trend-dialog';
import { TrendAxisDataAxisPathPointInsight } from '../../scorecard-mi2/trend-dialog/model/trend-axis-data-axis-path-point-insight.model ';
import { epicDependencies } from '../../store';
import { PerformanceMetricData } from '../models/performance-metric-data.model';
import { TranslationKeyMap } from '../my-performance-card.i18n';

/**
 * Generates normalized target points for the trend chart.
 * Normalizes the target values to the range of 0-10 based on the provided minimum and maximum values.
 *
 * @param trendData - The sorted trend data array containing performance metrics.
 * @param minVal - The minimum value in the dataset used for normalization.
 * @param maxVal - The maximum value in the dataset used for normalization.
 * @param color - (Optional) The color to assign to the points. Defaults to '#000'.
 * @returns An array of transformed target points with normalized `y` values.
 */
function createTargetPoints(
    trendData: PerformanceMetricData[],
    minVal: number,
    maxVal: number,
    color: string = '#000',
): TrendAxisDataAxisPathPointInsight[] {
    // Helper function for normalization
    const normalizeYValue = (value: number | null): number =>
        value != null ? ((value - minVal) / (maxVal - minVal)) * 10 : 0;

    return trendData
        .filter((trendItem) => trendItem.target != null)
        .map((trendItem) => ({
            x: trendItem.eventDate,

            y: normalizeYValue(trendItem.target),
            color,
            target:
                trendItem.target != null
                    ? formatNumber(trendItem.target, 'en-CA', '1.0-2')
                    : '-',
        }));
}

function getLeftTicks(metrics: PerformanceMetricData[]): AxisLeftTick[] {
    const tickColor = '#000';
    const totalTicks = 11;
    let minVal = Infinity;
    let maxVal = 0;

    metrics.forEach((metric) => {
        if (metric.actual !== undefined) {
            minVal = Math.min(minVal, metric.actual);
            maxVal = Math.max(maxVal, metric.actual);
        }
        if (metric.target !== undefined) {
            minVal = Math.min(minVal, metric.target);
            maxVal = Math.max(maxVal, metric.target);
        }
    });

    const adjustedMin = Math.floor(minVal / 10) * 10;
    const adjustedMax = Math.ceil(maxVal / 10) * 10;

    const labelStep = (adjustedMax - adjustedMin) / (totalTicks - 1);

    // Generate ticks
    const ticks = [];
    for (let tickId = 0; tickId < totalTicks; tickId++) {
        const labelValue = adjustedMin + tickId * labelStep;
        ticks.push({
            tickId,
            color: tickColor,
            label: Math.round(labelValue).toString(),
            isExtentTick: true,
        });
    }

    return ticks;
}

/**
 * Generates legends for the trend chart based on data paths.
 * @param data - The array of data paths.
 * @returns An array of legends.
 */
function getLegends(data: AxisDataAxisPath[]): Legend[] {
    const legends: Legend[] = [];

    for (const series of data) {
        const { key, points } = series;
        if (points == null || points.length === 0) {
            continue;
        }
        const color = points[0].color;
        legends.push({ color, label: key });
    }

    return legends;
}

/**
 * Generates trend data for performance metrics.
 * @param metrics - Pre-grouped array of PerformanceMetricData for a specific metric.
 * @returns Trend chart data object for visualization.
 */
export function generatePerformanceTrendData(
    metrics: PerformanceMetricData[],
): CoachingTrendData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    const paths: AxisDataAxisPath[] = [];

    const sortedMetrics = metrics
        .filter((data) => data.eventDate != null)
        .sort(
            (a, b) =>
                new Date(a.eventDate).getTime() -
                new Date(b.eventDate).getTime(),
        );

    const minVal = Math.min(
        ...metrics.map((data) =>
            Math.min(data.actual ?? Infinity, data.target ?? Infinity),
        ),
    );
    const maxVal = Math.max(
        ...metrics.map((data) =>
            Math.max(data.actual ?? -Infinity, data.target ?? -Infinity),
        ),
    );

    // Helper function for normalization
    const normalizeYValue = (value: number | null): number =>
        value != null ? ((value - minVal) / (maxVal - minVal)) * 10 : 0;

    // Generate chart points for actual values
    const points: TrendAxisDataAxisPathPointInsight[] = sortedMetrics.map(
        (data) => ({
            x: data.eventDate,
            y: normalizeYValue(data.actual), // Normalize y values to range 0-10
            actual: formatNumber(data.actual, 'en-CA', '1.0-1'),
            target: formatNumber(data.target, 'en-CA', '1.0-1'),
            color: '#1e60d4',
            name: metrics[0]?.metricName ?? '',
        }),
    );

    // Generate chart points for target values
    const targetPoints = createTargetPoints(sortedMetrics, minVal, maxVal);

    if (targetPoints.length > 0) {
        paths.push({
            key: translateService.instant(TranslationKeyMap._726_TARGET),
            points: targetPoints,
        });
    }
    if (points.length > 0) {
        paths.push({
            key: translateService.instant(TranslationKeyMap._725_ACTUAL),
            points,
        });
    }
    const leftTicks = getLeftTicks(sortedMetrics);
    const legends = getLegends(paths);
    const trend: string = translateService.instant(
        TranslationKeyMap._989_TREND,
    );
    const trendTitle = `${metrics[0]?.metricName ?? ''} ${trend}`;

    const rect: Rect = { width: '100%', height: 300, top: 20 };

    return {
        mode: TrendDialogMode.PERFORMANCE_DATA,
        showYAxisLabel2: false,
        leftTicks,
        rightTicks: [],
        paths,
        legends,
        rect,
        trendTitle,
    };
}
